import { fetchChatHistory } from "../../api/visitor";
import { getArticle, getArticlesByIds, getPopularArticles } from "../../api/helpcenter";
import { getPersistenceData, setPersistenceData } from "../../utils/persistenceStorage";
import { resolveOpeningTab } from "../../utils/shell/resolveOpeningTab";
import { countUnread } from "../../utils/shell/unread";
import { visibleCards, visibleTabs } from "../../utils/shell/visibleCards";
import { readUrlState, hasUrlState } from "../../utils/shell/urlState";
import { log2 } from "../../utils/log2";

const log = log2("shell");
const LAST_READ_KEY = "sps.shell.lastRead";
// Ayni oneri listesi (ayni kimlikler) oturumda bir kez cekilir; Ana Sayfa'ya her donuste tekrar istek atilmaz
let suggestedCache = { key: null, promise: null };
// Sohbet listesi kisa surede tekrar istenirse (sekme gecisi) eldeki kullanilir
const CONVERSATIONS_TTL_MS = 15000;
let conversationsRequest = { at: 0, promise: null };
// iframe ilk yuklendigindeki adres durumu; Shell adresi guncellemeye baslamadan once okunur
const initialUrlState = readUrlState();
const readLastRead = () => {
    try { return JSON.parse(getPersistenceData(LAST_READ_KEY) || "{}") || {}; } catch (e) { return {}; }
};
// Sunucu normalizer'inin hafif aynasi: eksik alanlar icin guvenli varsayilan
const normalizeClient = (hs) => {
    const src = hs && typeof hs === "object" ? hs : {};
    return {
        enabled: src.enabled === true,
        skipWhenActiveChat: src.skipWhenActiveChat !== false,
        alwaysSkip: src.alwaysSkip === true,
        tabs: { home: !(src.tabs && src.tabs.home === false), help: !(src.tabs && src.tabs.help === false) },
        header: { style: "gradient", imageUrl: "", imagePosition: { x: 50, y: 50 }, color: "", greetingSize: 22, headlineSize: 24, lineHeight: 1.25, showTeamAvatars: true, teamAvatars: [], languageContent: [], ...(src.header || {}) },
        cards: Array.isArray(src.cards) ? src.cards.filter((c) => c && c.type) : [],
    };
};

export default {
    namespaced: true,
    state: { activeTab: "home", chatOpen: true, draft: null, lastRead: readLastRead(), conversations: [], conversationsLoaded: false, suggested: [], booted: false, pendingArticle: null, pendingQuery: null, pendingNavigate: null, pendingCollection: null },
    getters: {
        config: (state, getters, rootState) => normalizeClient(state.draft || ((rootState.setting.settings || {}).webchat || {}).homeScreen),
        enabled: (state, getters) => getters.config.enabled,
        // Katalog durumu bilinmeden (null) yardim merkezi kapali sayilir; boot onu her zaman yukler
        helpCenterEnabled: (state, getters, rootState) => rootState.catalog.enabled === true && rootState.catalog.hasContent,
        tabs: (state, getters) => visibleTabs(getters.config, { helpCenterEnabled: getters.helpCenterEnabled }),
        cards: (state, getters) => visibleCards(getters.config, { helpCenterEnabled: getters.helpCenterEnabled }),
        unreadCount: (state) => countUnread(state.conversations, state.lastRead),
        hasActiveChat: (state, getters, rootState) => !!rootState.chat.conversationId && rootState.chat.connected,
        showTabBar: (state, getters) => getters.enabled && !state.chatOpen && getters.tabs.length > 1,
    },
    mutations: {
        SET_TAB: (s, tab) => { s.activeTab = tab; },
        SET_CHAT_OPEN: (s, v) => { s.chatOpen = v; },
        SET_DRAFT: (s, d) => { s.draft = d; },
        SET_LAST_READ: (s, { id, at }) => { s.lastRead = { ...s.lastRead, [id]: at }; },
        SET_CONVERSATIONS: (s, list) => { s.conversations = list; s.conversationsLoaded = true; },
        SET_SUGGESTED: (s, list) => { s.suggested = list; },
        SET_BOOTED: (s) => { s.booted = true; },
        SET_PENDING_ARTICLE: (s, a) => { s.pendingArticle = a; },
        SET_PENDING_QUERY: (s, q) => { s.pendingQuery = q; },
        SET_PENDING_NAVIGATE: (s, p) => { s.pendingNavigate = p; },
        SET_PENDING_COLLECTION: (s, id) => { s.pendingCollection = id; },
    },
    actions: {
        async boot({ state, getters, commit, dispatch, rootState }) {
            if (state.booted) return;
            // Ayarlar sunucudan gelmeden karar verme; fetched olunca Shell yeniden cagirir
            if (!rootState.setting.fetched) { commit("SET_CHAT_OPEN", true); return; }
            commit("SET_BOOTED");
            if (!getters.enabled) { commit("SET_CHAT_OPEN", true); commit("SET_PENDING_NAVIGATE", null); return; }
            // Katalog durumu Home'un ilk boyamasindan once bilinmeli; yoksa Yardim sekmesi/makale karti yanip sonuyor
            // Katalog cekilemezse enabled null kalmasin; yoksa bekleyen navigate sonsuza kadar askida kalir
            // Ana Sayfa'daki makale karti icin oneriler katalogla ayni anda istenir; kart acilinca hazir olur
            const articlesCard = (getters.config.cards || []).find((c) => c.type === "articles" && c.enabled !== false);
            if (articlesCard) dispatch("loadSuggested", articlesCard).catch(() => {});
            await Promise.all([
                dispatch("loadConversations").catch(() => {}),
                dispatch("catalog/getCategories", null, { root: true }).catch((e) => {
                    log("boot: katalog yuklenemedi", e && e.message);
                    commit("catalog/SET_ENABLED", false, { root: true });
                }),
            ]);
            // Bekleyen deep link varsa varsayilan acilis sekmesinden once o kazanir
            if (state.pendingNavigate) {
                const n = state.pendingNavigate;
                commit("SET_PENDING_NAVIGATE", null);
                commit("SET_CHAT_OPEN", false);
                await dispatch("navigate", n);
                return;
            }
            // Adreste kayitli ekran (yenileme ya da yeni sekme) varsayilan acilistan once gelir; onizlemede kullanilmaz
            const fromUrl = rootState.app.previewMode ? null : initialUrlState;
            if (hasUrlState(fromUrl)) {
                log("boot: adresten", fromUrl);
                if (fromUrl.chat) { dispatch("openChat"); return; }
                commit("SET_CHAT_OPEN", false);
                if (fromUrl.article || fromUrl.collection) { await dispatch("navigate", { article: fromUrl.article, collection: fromUrl.collection }); return; }
                commit("SET_TAB", getters.tabs.includes(fromUrl.tab) ? fromUrl.tab : getters.tabs[0] || "messages");
                return;
            }
            const tab = resolveOpeningTab({ config: getters.config, hasActiveChat: getters.hasActiveChat, unreadCount: getters.unreadCount });
            log("boot", tab);
            if (tab === "chat") { commit("SET_CHAT_OPEN", true); commit("SET_TAB", "messages"); return; }
            commit("SET_CHAT_OPEN", false);
            commit("SET_TAB", getters.tabs.includes(tab) ? tab : "messages");
        },
        // Canli ayar degisikligi: katalog yenilenir; Ana Sayfa yeni acildiysa ve suren sohbet yoksa acilis sekmesine gecilir
        async settingsChanged({ getters, commit, dispatch }, { wasEnabled } = {}) {
            await dispatch("catalog/getCategories", null, { root: true }).catch(() => {});
            if (wasEnabled || !getters.enabled || getters.hasActiveChat) return;
            const tab = resolveOpeningTab({ config: getters.config, hasActiveChat: false, unreadCount: getters.unreadCount });
            if (tab === "chat") return;
            commit("SET_CHAT_OPEN", false);
            commit("SET_TAB", getters.tabs.includes(tab) ? tab : "messages");
        },
        openTab({ commit }, tab) { commit("SET_CHAT_OPEN", false); commit("SET_TAB", tab); },
        setPendingQuery({ commit }, q) { commit("SET_PENDING_QUERY", q || null); },
        async navigate({ state, getters, commit, dispatch, rootState }, payload) {
            const p = payload || {};
            // SET_BOOTED, katalog Promise.all'i bitmeden commit edilir; o gecici aralikta da bekletir (artik hep sonuclanir, yukaridaki catch sayesinde sonsuza kalmaz)
            if (!state.booted || rootState.catalog.enabled === null) { commit("SET_PENDING_NAVIGATE", p); return; }
            if (!getters.enabled) { dispatch("openChat"); return; }
            if (p.article) {
                try {
                    const r = await getArticle(String(p.article), null);
                    const a = r.data || {};
                    commit("SET_PENDING_ARTICLE", { _id: a._id, collectionId: a.collectionId, slug: a.slug, name: "" });
                    // yalniz baglantiyla erisilen makale: liste bos olsa da Yardim sekmesi acilabilsin
                    commit("catalog/SET_HAS_CONTENT", true, { root: true });
                } catch (e) { log("navigate: makale bulunamadi", p.article); }
                p.tab = "help";
            }
            else if (p.collection) { commit("SET_PENDING_COLLECTION", String(p.collection)); p.tab = "help"; }
            if (p.query) { commit("SET_PENDING_QUERY", String(p.query)); p.tab = "help"; }
            const tabs = getters.tabs;
            if (p.tab && tabs.includes(p.tab)) dispatch("openTab", p.tab);
            else if (p.tab === "help" && !tabs.includes("help")) dispatch("openTab", tabs.includes("home") ? "home" : "messages");
        },
        openChat({ commit, rootState, dispatch }) {
            commit("SET_CHAT_OPEN", true);
            commit("SET_TAB", "messages");
            if (rootState.chat.conversationId) dispatch("markRead", rootState.chat.conversationId);
        },
        closeChat({ commit, getters, dispatch, rootState }) {
            if (!getters.enabled) return;
            // sohbetten donuste liste tazelensin (yeni sohbet ya da mesaj olabilir)
            conversationsRequest = { at: 0, promise: null };
            // Okurken gelen mesajlar okunmus sayilir; geri donunce kirmizi nokta kalmaz
            if (rootState.chat.conversationId) dispatch("markRead", rootState.chat.conversationId);
            commit("SET_CHAT_OPEN", false);
            commit("SET_TAB", "messages");
        },
        setPendingArticle({ commit }, a) { commit("SET_PENDING_ARTICLE", a && a._id ? a : null); },
        clearPendingCollection({ commit }) { commit("SET_PENDING_COLLECTION", null); },
        clearPendingArticle({ commit }) { commit("SET_PENDING_ARTICLE", null); },
        markRead({ commit, state }, id) {
            const at = Date.now();
            commit("SET_LAST_READ", { id, at });
            try { setPersistenceData(LAST_READ_KEY, JSON.stringify(state.lastRead), true); } catch (e) { /* depolama kapali */ }
        },
        async loadConversations({ commit, rootState }, { force = false } = {}) {
            const site = rootState.site.site;
            const visitorId = rootState.chat.visitorId;
            if (!site || !site._id || !visitorId) { commit("SET_CONVERSATIONS", []); return; }
            // ayni anda gelen ya da kisa sure once yapilmis istek tekrar edilmez
            if (!force && conversationsRequest.promise && Date.now() - conversationsRequest.at < CONVERSATIONS_TTL_MS) return conversationsRequest.promise;
            conversationsRequest = {
                at: Date.now(),
                promise: fetchChatHistory(site._id, visitorId, 1, 20)
                    .then((res) => commit("SET_CONVERSATIONS", (res.data && res.data.conversations) || []))
                    .catch((e) => {
                        // Liste gelmese de yuklendi sayilir; Mesajlar bos durumu gosterir
                        log("loadConversations failed", e && e.message);
                        conversationsRequest = { at: 0, promise: null };
                        commit("SET_CONVERSATIONS", []);
                    }),
            };
            return conversationsRequest.promise;
        },
        loadSuggested({ commit }, card) {
            const ids = (card && card.suggestedArticleIds) || [];
            const key = ids.length ? ids.join(",") : "popular";
            if (suggestedCache.key === key && suggestedCache.promise) return suggestedCache.promise;
            const promise = (ids.length ? getArticlesByIds(ids) : getPopularArticles(5))
                .then((res) => commit("SET_SUGGESTED", Array.isArray(res.data) ? res.data : []))
                .catch((e) => {
                    suggestedCache = { key: null, promise: null };
                    throw e;
                });
            suggestedCache = { key, promise };
            return promise;
        },
        setDraft({ commit, dispatch, rootState }, payload) {
            if (!rootState.app.previewMode) return;
            // Onizlemede boot erken donebilir; katalog durumu bilinmiyorsa burada yuklenir
            if (rootState.catalog.enabled === null) dispatch("catalog/getCategories", null, { root: true }).catch(() => {});
            commit("SET_DRAFT", payload && typeof payload === "object" ? payload : null);
            commit("SET_CHAT_OPEN", false);
            commit("SET_TAB", "home");
        },
    },
};



// WEBPACK FOOTER //
// ./src/store/modules/shell.js